import { ChangeDetectionStrategy, Component, ElementRef, Input } from '@angular/core';
import { safeJsonInput } from '../../../shared/_utils/safe-json-input';

@Component({
    selector: 'gtw-data-filters',
    templateUrl: './data-filters.component.html',
    styleUrls: ['./data-filters.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class DataFiltersComponent {
  baseUrls: any = {};
  headerData: any = {};
  rowData: any = {};
  filterParams: any = {};
  filterObjs: any = { filters: [], toggleSelectedFilter: [], loading: false, showFilters: true };

  loading = false;
  showFilters = true;
  filterMatch = true;
  screenKey = 0;

  constructor(private elementRef: ElementRef<HTMLElement>) {}

  @Input('base-urls')
  set _base_urls(baseUrls: string) {
    const parsed = typeof baseUrls === 'string'
      ? safeJsonInput<any>(baseUrls, null, 'data-filters.base-urls')
      : baseUrls;
    if (parsed && Object.keys(parsed).length > 0) {
      this.baseUrls = parsed;
    }
  }

  @Input('header-data')
  set _header_data(headerData: string) {
    const parsed = typeof headerData === 'string'
      ? safeJsonInput<any>(headerData, null, 'data-filters.header-data')
      : headerData;
    if (parsed && Object.keys(parsed).length > 0) {
      this.headerData = parsed;
      this.screenKey = Number(parsed?.globalParams?.screen_key || 0);
    }
  }

  @Input('row-data')
  set _row_data(rowData: string) {
    this.rowData = typeof rowData === 'string'
      ? safeJsonInput<any>(rowData, {}, 'data-filters.row-data') || {}
      : rowData || {};
  }

  @Input('filter-params')
  set _filter_params(filterParams: string) {
    const parsed = typeof filterParams === 'string'
      ? safeJsonInput<any>(filterParams, null, 'data-filters.filter-params')
      : filterParams;
    if (parsed && Object.keys(parsed).length > 0) {
      this.filterParams = parsed;
      if (!this.screenKey) {
        this.screenKey = Number(parsed?.screen_key || 0);
      }
    }
  }

  @Input('filter-objs')
  set _filter_objs(filterObjs: string) {
    const parsed = typeof filterObjs === 'string'
      ? safeJsonInput<any>(filterObjs, null, 'data-filters.filter-objs')
      : filterObjs;
    if (parsed && Object.keys(parsed).length > 0) {
      this.filterObjs = parsed;
      this.loading = !!parsed.loading;
      this.showFilters = parsed.showFilters !== false;
    }
  }

  @Input('filter-match')
  set _filter_match(value: string | boolean) {
    this.filterMatch = value === true || value === 'true';
  }

  @Input('screen-key')
  set _screen_key(value: string | number) {
    const parsed = Number(value);
    if (!Number.isNaN(parsed) && parsed > 0) {
      this.screenKey = parsed;
    }
  }

  get filterEntries(): Array<{ key: string; value: any }> {
    const obj = this.filterParams || {};
    return Object.keys(obj).map((key) => ({ key, value: obj[key] }));
  }

  get primaryFilters(): any[] {
    const list = this.filterObjs?.filters;
    return Array.isArray(list) ? list.filter((f: any) => !f?.is_or_conditional_filter) : [];
  }

  get toggleFilters(): any[] {
    const list = this.filterObjs?.toggleSelectedFilter;
    return Array.isArray(list) ? list : [];
  }

  get hasFilterModel(): boolean {
    return this.primaryFilters.length > 0 || this.toggleFilters.length > 0;
  }

  getSelectedLabel(filter: any): string {
    if (!filter) {
      return '';
    }
    const selected = filter.selected;
    if (Array.isArray(selected) && selected.length > 0) {
      return selected.join(', ');
    }
    if (selected && typeof selected === 'object') {
      return selected.VALUE || selected.value || selected.NAME || '';
    }
    return selected ? String(selected) : '';
  }

  isSingleSelectFilter(filter: any): boolean {
    const type = String(filter?.ui_type || '').toLowerCase();
    return type.includes('select-single');
  }

  isMeDropFilter(filter: any): boolean {
    const type = String(filter?.ui_type || '').toLowerCase();
    return type.includes('me-drop');
  }

  getFilterOptions(filter: any): any[] {
    return Array.isArray(filter?.values) ? filter.values : [];
  }

  getOptionValue(option: any): string {
    if (option === null || option === undefined) {
      return '';
    }
    if (typeof option === 'object') {
      return String(option?.VALUE ?? option?.value ?? option?.key ?? option?.id ?? '');
    }
    return String(option);
  }

  getOptionLabel(option: any): string {
    if (option === null || option === undefined) {
      return '';
    }
    if (typeof option === 'object') {
      return String(
        option?.NAME ??
          option?.name ??
          option?.label ??
          option?.DESC ??
          option?.description ??
          option?.VALUE ??
          option?.value ??
          option?.key ??
          ''
      );
    }
    return String(option);
  }

  getSingleSelectedValue(filter: any): string {
    const selected = filter?.selected;
    if (selected === null || selected === undefined) {
      return '';
    }
    if (Array.isArray(selected)) {
      return selected.length > 0 ? String(selected[0]) : '';
    }
    if (typeof selected === 'object') {
      return String(selected?.VALUE ?? selected?.value ?? selected?.key ?? '');
    }
    return String(selected);
  }

  onSingleFilterChange(filter: any, selectedValue: string): void {
    this.emitCustomEvent('data-filters-selection-changed', {
      filter_key: filter?.filter_key,
      param_name: filter?.param_name,
      selection_type: 'single',
      selected_value: selectedValue,
    });
  }

  getMeDropOptions(filter: any): Array<{ key: string; name: string; level: string; depth: number }> {
    return this.buildMeTreeIndex(filter).flat;
  }

  getMeSelectedKeys(filter: any): string[] {
    const selected = filter?.selected;
    if (!selected) {
      return [];
    }
    if (Array.isArray(selected)) {
      return selected.map((item: any) =>
        typeof item === 'object' ? String(item?.key ?? item?.VALUE ?? item?.value ?? '') : String(item)
      );
    }
    if (typeof selected === 'object') {
      return [String(selected?.key ?? selected?.VALUE ?? selected?.value ?? '')];
    }
    return [String(selected)];
  }

  isMeOptionSelected(filter: any, optionKey: string): boolean {
    return this.getMeSelectedKeys(filter).includes(String(optionKey));
  }

  onMeFilterSelectionChange(filter: any, optionKey: string, checked: boolean): void {
    const treeIndex = this.buildMeTreeIndex(filter);
    const selectedSet = new Set(this.getMeSelectedKeys(filter).map((k) => String(k)));
    const key = String(optionKey);
    const descendants = this.collectDescendantsKeys(key, treeIndex.byKey);

    if (checked) {
      selectedSet.add(key);
      descendants.forEach((d) => selectedSet.add(d));
    } else {
      selectedSet.delete(key);
      descendants.forEach((d) => selectedSet.delete(d));
    }

    // Keep parent selection consistent with all-children-selected rule.
    let parentKey = treeIndex.byKey[key]?.parentKey || '';
    while (parentKey) {
      const parentNode = treeIndex.byKey[parentKey];
      if (!parentNode) {
        break;
      }
      const allChildrenSelected = parentNode.childrenKeys.every((child) => selectedSet.has(child));
      if (allChildrenSelected) {
        selectedSet.add(parentKey);
      } else {
        selectedSet.delete(parentKey);
      }
      parentKey = parentNode.parentKey || '';
    }

    const next = Array.from(selectedSet);

    // Keep local visual state in sync.
    filter.selected = next;

    this.emitCustomEvent('data-filters-selection-changed', {
      filter_key: filter?.filter_key,
      param_name: filter?.param_name,
      selection_type: 'multi',
      selected_keys: next,
    });
  }

  onMeFilterSelectAll(filter: any): void {
    const treeIndex = this.buildMeTreeIndex(filter);
    const next = treeIndex.flat.map((item) => item.key);
    filter.selected = next;
    this.emitCustomEvent('data-filters-selection-changed', {
      filter_key: filter?.filter_key,
      param_name: filter?.param_name,
      selection_type: 'multi',
      selected_keys: next,
    });
  }

  onMeFilterUnselectAll(filter: any): void {
    filter.selected = [];
    this.emitCustomEvent('data-filters-selection-changed', {
      filter_key: filter?.filter_key,
      param_name: filter?.param_name,
      selection_type: 'multi',
      selected_keys: [],
    });
  }

  meOptionIndent(option: any): number {
    return Number(option?.depth || 0) * 12;
  }

  private buildMeTreeIndex(filter: any): {
    flat: Array<{ key: string; name: string; level: string; depth: number }>;
    byKey: Record<string, { parentKey: string; childrenKeys: string[] }>;
  } {
    const flat: Array<{ key: string; name: string; level: string; depth: number }> = [];
    const byKey: Record<string, { parentKey: string; childrenKeys: string[] }> = {};

    const walk = (nodes: any[], depth: number, parentKey: string) => {
      (nodes || []).forEach((item: any) => {
        const key = String(item?.key ?? item?.VALUE ?? '');
        if (!key) {
          return;
        }
        const children = Array.isArray(item?.children) ? item.children : [];
        const childrenKeys = children
          .map((c: any) => String(c?.key ?? c?.VALUE ?? ''))
          .filter((k: string) => !!k);

        byKey[key] = { parentKey, childrenKeys };
        flat.push({
          key,
          name: String(item?.name ?? item?.NAME ?? item?.key ?? ''),
          level: String(item?.level ?? ''),
          depth,
        });

        if (children.length > 0) {
          walk(children, depth + 1, key);
        }
      });
    };

    walk(Array.isArray(filter?.values) ? filter.values : [], 0, '');
    return { flat, byKey };
  }

  private collectDescendantsKeys(
    key: string,
    byKey: Record<string, { parentKey: string; childrenKeys: string[] }>
  ): string[] {
    const result: string[] = [];
    const stack = [...(byKey[key]?.childrenKeys || [])];
    while (stack.length > 0) {
      const current = stack.pop() as string;
      result.push(current);
      const nextChildren = byKey[current]?.childrenKeys || [];
      nextChildren.forEach((child) => stack.push(child));
    }
    return result;
  }

  refresh(): void {
    this.emitCustomEvent('data-filters-refresh-grid', {
      refresh: true,
      filterParams: this.filterParams,
    });
  }

  downloadSelectedFiltersData(): void {
    this.emitCustomEvent('data-filters-download-selected', {
      downloadSelectedFiltersGridData: true,
      filterParams: this.filterParams,
    });
  }

  launchCustomModalForSpecificScreen(screenId: number): void {
    this.emitCustomEvent('data-filters-open-custom-modal', { screenId });
  }

  openDocument(): void {
    this.emitCustomEvent('data-filters-open-document', {});
  }

  private emitCustomEvent(eventName: string, detail: any): void {
    const event = new CustomEvent(eventName, {
      detail,
      bubbles: true,
    });
    this.elementRef.nativeElement.dispatchEvent(event);
  }
}
